/**
 * Detection helpers for the free-tier plan-limit errors returned by the API.
 *
 * Phase 2b server (keeptouch-api) refuses a limited action with HTTP 402 and
 * the standard error envelope `{ code, message, params, statusCode }`. The
 * `code` is one of the stable identifiers below. When the app sees one of
 * these, it should surface the Premium paywall instead of a raw error toast.
 */

export const PLAN_LIMIT_ERROR_CODES = [
  'PLAN_LIMIT_CONTACTS_REACHED',
  'PLAN_LIMIT_GHOST_PROFILES_REACHED',
  'PLAN_LIMIT_NOTES_REACHED',
  'PLAN_LIMIT_REMINDERS_REACHED',
  'PLAN_FEATURE_REMINDERS_DISABLED',
  'PLAN_FEATURE_EXPORT_DISABLED',
  'PLAN_FEATURE_UNIPILE_DISABLED',
  'PLAN_LIMIT_CARD_SCANS_REACHED',
  'PLAN_FEATURE_CARD_SCAN_DISABLED',
] as const

export type PlanLimitCode = (typeof PLAN_LIMIT_ERROR_CODES)[number]

const PLAN_LIMIT_HTTP_STATUS = 402

interface ApiErrorEnvelope {
  code?: string
  message?: string
  params?: Record<string, unknown>
  statusCode?: number
}

interface AxiosLikeError {
  status?: number
  response?: {
    status?: number
    data?: ApiErrorEnvelope | string
  }
}

const readEnvelope = (error: unknown): ApiErrorEnvelope | undefined => {
  const data = (error as AxiosLikeError | undefined)?.response?.data
  // Requests made with `responseType: 'text'` (e.g. the vCard export) get the
  // JSON error envelope as a raw string.
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)
      return parsed && typeof parsed === 'object' ? (parsed as ApiErrorEnvelope) : undefined
    } catch {
      return undefined
    }
  }
  return data
}

/**
 * The stable plan-limit code carried by the error, or `undefined` when the
 * error is not one of the known plan-limit codes.
 */
export function getPlanLimitCode(error: unknown): PlanLimitCode | undefined {
  const code = readEnvelope(error)?.code
  if (code && (PLAN_LIMIT_ERROR_CODES as readonly string[]).includes(code)) {
    return code as PlanLimitCode
  }
  return undefined
}

/**
 * The `params` the API attached to the plan-limit error (e.g. `{ limit }`) —
 * used to template the paywall / toast copy.
 */
export function getPlanLimitParams(error: unknown): Record<string, unknown> | undefined {
  return readEnvelope(error)?.params
}

/**
 * True when the error is a free-tier plan-limit refusal — recognised either by
 * one of the stable codes above or by the 402 status (envelope or HTTP).
 */
export function isPlanLimitError(error: unknown): boolean {
  if (getPlanLimitCode(error)) {
    return true
  }
  const axiosError = error as AxiosLikeError | undefined
  const httpStatus = axiosError?.response?.status ?? axiosError?.status
  const envelopeStatus = readEnvelope(error)?.statusCode
  return httpStatus === PLAN_LIMIT_HTTP_STATUS || envelopeStatus === PLAN_LIMIT_HTTP_STATUS
}
